import { CoursePriceType } from './course-policy';

export const CoursePerkMode = {
  FREE: 'free',
  MEMBER_PRICE: 'member_price',
  PUBLIC_PRICE: 'public_price',
} as const;

export type CoursePerkModeValue = (typeof CoursePerkMode)[keyof typeof CoursePerkMode];

export type CoursePerkPricingInput = {
  perkMode?: string | null;
  publicPriceAmountCents?: number | null;
  memberPriceAmountCents?: number | null;
  coursePriceType?: string | null;
};

export function normalizeCoursePerkMode(mode?: string | null): CoursePerkModeValue {
  if (mode === CoursePerkMode.MEMBER_PRICE) return CoursePerkMode.MEMBER_PRICE;
  if (mode === CoursePerkMode.PUBLIC_PRICE) return CoursePerkMode.PUBLIC_PRICE;
  return CoursePerkMode.FREE;
}

export function grantsFreeCoursePerkEntitlement(perkMode?: string | null): boolean {
  return normalizeCoursePerkMode(perkMode) === CoursePerkMode.FREE;
}

export function perkModeRequiresCheckout(perkMode?: string | null): boolean {
  const mode = normalizeCoursePerkMode(perkMode);
  return mode === CoursePerkMode.MEMBER_PRICE || mode === CoursePerkMode.PUBLIC_PRICE;
}

export function resolveCoursePerkCheckoutAmountCents(input: CoursePerkPricingInput): number | null {
  const mode = normalizeCoursePerkMode(input.perkMode);
  const publicPrice = input.publicPriceAmountCents ?? null;

  if (input.coursePriceType === CoursePriceType.FREE) {
    return null;
  }

  if (mode === CoursePerkMode.PUBLIC_PRICE) {
    return publicPrice;
  }

  if (mode === CoursePerkMode.MEMBER_PRICE) {
    return input.memberPriceAmountCents ?? null;
  }

  return null;
}

export function validateCoursePerkSettings(input: {
  perkMode?: string | null;
  coursePriceType?: string | null;
  publicPriceAmountCents?: number | null;
  memberPriceAmountCents?: number | null;
}): string | null {
  const mode = normalizeCoursePerkMode(input.perkMode);
  const isPaidCourse = input.coursePriceType === CoursePriceType.ONE_TIME;

  if (mode === CoursePerkMode.FREE) {
    return null;
  }

  if (!isPaidCourse) {
    return 'Paid perk modes require a one-time priced course';
  }

  if (input.publicPriceAmountCents == null || input.publicPriceAmountCents <= 0) {
    return 'Course public price must be configured for paid perk modes';
  }

  if (mode === CoursePerkMode.MEMBER_PRICE) {
    if (input.memberPriceAmountCents == null || input.memberPriceAmountCents <= 0) {
      return 'Member price is required for member_price perk mode';
    }
    if (input.memberPriceAmountCents > input.publicPriceAmountCents) {
      return 'Member price cannot exceed the public course price';
    }
  }

  return null;
}

export function resolveClassroomCourseAccess(params: {
  perkMode?: string | null;
  membershipEligible: boolean;
  hasEnrollment: boolean;
}): {
  entitled: boolean;
  requiresPurchase: boolean;
} {
  if (params.hasEnrollment) {
    return { entitled: true, requiresPurchase: false };
  }

  if (!params.membershipEligible) {
    return { entitled: false, requiresPurchase: false };
  }

  if (grantsFreeCoursePerkEntitlement(params.perkMode)) {
    return { entitled: true, requiresPurchase: false };
  }

  return { entitled: false, requiresPurchase: perkModeRequiresCheckout(params.perkMode) };
}
